/**
 * useTheme.js
 * FILE VERSION: USETHEME-v1.7.41.90
 * Ledger #1468
 *
 * Per-user Light/Medium/Dark foundation tone.
 *
 * Light, Medium and Dark are saved in the existing per-user profile field.
 * An absent or unsupported value resolves to Light; the navy menu stays
 * constant while shared surfaces use the selected tone.
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabase';

function apply(mode) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', mode === 'dark' ? 'dark' : 'light');
  document.documentElement.setAttribute('data-tone', mode);
}

function resolvePreference(pref) {
  if (pref === 'system') {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return TONES.includes(pref) ? pref : 'light';
}

export const TONES = ['light', 'medium', 'dark'];

export function useTheme() {
  const [preference, setPreference] = useState('light');
  const [resolved, setResolved] = useState('light');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apply(resolved);
  }, [resolved]);

  // Keep legacy stored `system` preferences aligned with the current OS theme.
  useEffect(() => {
    if (preference !== 'system' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setResolved(resolvePreference('system'));
    media.addEventListener?.('change', update);
    return () => media.removeEventListener?.('change', update);
  }, [preference]);

  // Load the stored preference.
  useEffect(() => {
    (async () => {
      try {
        // getSession() reads the signed-in session from the browser; getUser() is a network call (/auth/v1/user) that holds the auth lock while it runs, and every other
        // request waits for that lock to get its token: on a cold load it held the Lead Management reads back by about 130 ms (#2563). The row is read as this user either way.
        const { data: sess } = await supabase.auth.getSession();
        const uid = sess?.session?.user?.id;
        if (!uid) { setLoading(false); return; }
        const { data } = await supabase
          .from('user_profiles').select('theme_preference')
          .eq('id', uid).maybeSingle();
        const pref = ['light', 'medium', 'dark', 'system'].includes(data?.theme_preference) ? data.theme_preference : 'light';
        setPreference(pref);
        setResolved(resolvePreference(pref));
      } catch (_e) {
        // Never block the app on a theme lookup. Worst case the user
        // gets their system preference, which is the default anyway.
      }
      setLoading(false);
    })();
  }, []);

  const setTheme = useCallback(async (pref) => {
    // Apply immediately, save after. A toggle that waits on a round
    // trip feels broken, and this is a control used many times a day.
    setPreference(pref);
    setResolved(pref);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const uid = sess?.session?.user?.id;
      if (!uid) return;
      const { error } = await supabase.from('user_profiles')
        .update({ theme_preference: pref })
        .eq('id', uid);
      if (error) console.warn('Tone was not saved:', error.message);
    } catch (_e) {
      // Local state already changed, so the user sees what they asked
      // for. It just will not follow them to another device.
    }
  }, []);

  const cycle = useCallback(() => setTheme(preference === 'light' ? 'medium' : preference === 'medium' ? 'dark' : 'light'), [preference, setTheme]);

  return { preference, resolved, setTheme, cycle, loading };
}
